import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Container
} from '@mui/material';
import {
  Add as AddIcon,
  FolderOpen as EmptyFolderIcon
} from '@mui/icons-material';

import { Group, DashboardLayout, DashboardTheme } from '../types';
import { useResponsive } from '../utils/mobileUtils';
import GroupCard from './Group/GroupCard';
import { useDashboard } from '../hooks/useDashboard';

interface DashboardGridProps {
  groups: Group[];
  layout: DashboardLayout;
  theme: DashboardTheme;
  editMode: boolean;
}

const DashboardGrid: React.FC<DashboardGridProps> = ({
  groups,
  layout,
  theme,
  editMode
}) => {
  const { isMobile } = useResponsive();
  const { config } = useDashboard();
  
  // Get theme colors
  const themeColors = config?.theme?.customColors || {} as NonNullable<DashboardTheme['customColors']>;

  // Sort groups by order
  const sortedGroups = [...groups].sort((a, b) => a.order - b.order);

  const handleNewGroup = () => {
    const event = new CustomEvent('dashboard:new-group');
    document.dispatchEvent(event);
  };

  if (groups.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ textAlign: 'center', py: isMobile ? 4 : 8 }}>
        <EmptyFolderIcon 
          sx={{ 
            fontSize: isMobile ? 60 : 80, 
            color: 'text.disabled',
            mb: 2 
          }} 
        />
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          color="text.secondary" 
          gutterBottom
        >
          Welcome to Your Dashboard
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ 
            mb: 3, 
            maxWidth: isMobile ? 300 : 400, 
            mx: 'auto',
            fontSize: isMobile ? '0.875rem' : '1rem'
          }}
        >
          Get started by creating your first group to organize your favorite links and tools.
        </Typography>
        
        {editMode ? (
          <Paper
            onClick={handleNewGroup}
            elevation={1}
            sx={{
              p: 3,
              maxWidth: 300,
              mx: 'auto',
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: themeColors.addButton || '#6b7280',
              backgroundColor: 'rgba(107, 114, 128, 0.1)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(107, 114, 128, 0.2)',
                transform: 'translateY(-2px)',
                boxShadow: (theme) => theme.shadows[4],
              },
            }}
          >
            <AddIcon sx={{ fontSize: 40, color: themeColors.addButton || '#6b7280', mb: 1 }} />
            <Typography variant="h6" sx={{ color: themeColors.addButton || '#6b7280' }}>
              Create Your First Group
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Click here to add a new group
            </Typography>
          </Paper>
        ) : (
          <Alert severity="info" sx={{ maxWidth: 400, mx: 'auto' }}>
            <Typography variant="body2">
              No groups have been created yet. Enable edit mode to start building your dashboard.
            </Typography>
          </Alert>
        )}
      </Container>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Groups stacked vertically */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
        {sortedGroups.map((group, index) => (
          <GroupCard
            key={group.id}
            group={group}
            editMode={editMode}
            index={index}
            layout={layout}
            theme={theme}
          />
        ))}

        {/* Add New Group Card */}
        {editMode && (
          <Paper
            onClick={handleNewGroup}
            elevation={1}
            sx={{
              p: 3,
              height: 120,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: themeColors.addButton || '#6b7280',
              backgroundColor: 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: themeColors.addButton || '#6b7280',
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                transform: 'translateY(-2px)',
                '& .add-icon': {
                  color: themeColors.addButton || '#6b7280',
                  transform: 'scale(1.1)',
                },
                '& .add-text': {
                  color: themeColors.addButton || '#6b7280',
                },
              },
            }}
          >
            <AddIcon 
              className="add-icon"
              sx={{ 
                fontSize: 32, 
                color: themeColors.addButton || '#6b7280',
                mb: 1,
                transition: 'all 0.2s ease',
              }} 
            />
            <Typography 
              className="add-text"
              variant="subtitle1" 
              sx={{ 
                color: themeColors.addButton || '#6b7280',
                transition: 'color 0.2s ease',
                fontWeight: 600
              }}
            >
              Add Group
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Empty State for Individual Groups */}
      {groups.length > 0 && groups.every(group => group.tiles.length === 0) && !editMode && (
        <Box sx={{ textAlign: 'center', mt: isMobile ? 2 : 4 }}>
          <Alert severity="info" sx={{ maxWidth: isMobile ? 350 : 500, mx: 'auto' }}>
            <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
              Your groups are empty. Enable edit mode to start adding tiles to your groups.
            </Typography>
          </Alert>
        </Box>
      )}

    </Box>
  );
};

export default DashboardGrid;